import type { KeyEventArgs, MouseEventArgs, TextInputArgs } from "../core/events.js";
import { Control, type ControlOptions } from "../core/Control.js";
import { ScrollableBase } from "./ScrollableBase.js";
import type { Rect } from "../../common/geometry.js";
import { visibleRowRange } from "../core/virtualWindow.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import {
  buildNavigationIndex,
  filterNavigationIndex,
  windowOf,
  type NavEntry,
} from "./navigationEntries.js";

export interface NavigationIndexOptions extends ControlOptions {
  /** Rows shown before it starts scrolling. */
  maxRows?: number;
  onChosen?: (entry: NavEntry) => void;
}

const INDENT = 14;
const PADDING = 4;

/**
 * An outline of the desktop, drawn on the desktop.
 *
 * The keyboard counterpart to the screen-reader mirror, and useful to a
 * different person: somebody who can see the screen perfectly well and cannot
 * reach a window buried three behind the front one without a lot of Tab.
 * Alt+Tab shows you windows; this shows you what is inside them.
 *
 * It is an ordinary control drawn as quads like everything else, so it does
 * nothing at all for a screen reader directly - what it does do is mirror
 * itself, through the same layer as every other control, because its rows are
 * items and its names come from `describeAccessible`. One definition of what
 * a thing is called, used by both.
 *
 * Shown through `Desktop.showNavigationIndex`, which puts it on the popup
 * stack: Escape, press-outside and foreground z-order all come from there
 * rather than being written again here.
 */
export class NavigationIndex extends Control {
  #entries: NavEntry[] = [];
  #shown: NavEntry[] = [];
  #query = "";
  #highlight = 0;
  #scroll = 0;
  #rowHeight = 0;

  maxRows: number;
  onChosen: ((entry: NavEntry) => void) | null;

  constructor(options: NavigationIndexOptions = {}) {
    super({ width: 320, height: 240, tabStop: true, ...options });
    this.maxRows = options.maxRows ?? 14;
    this.onChosen = options.onChosen ?? null;
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("textinput", (e) => this.#onTextInput(e));
  }

  /** Type-to-filter, which also means a phone raises its keyboard for this. */
  override get acceptsTextInput(): boolean {
    return true;
  }

  /** Read the desktop and size to fit. Called each time it is shown. */
  refresh(): void {
    const desktop = this.desktop;
    if (!desktop) return;
    // Excluding itself: it is a foreground child of the desktop like any
    // other, and an index that offers to take you to the index is a joke
    // that stops being funny on the second read.
    this.#entries = buildNavigationIndex(desktop, desktop.focusManager.focused, this);
    this.#applyFilter();
    this.#rowHeight = desktop.theme.metrics.listItemHeight;
    const rows = Math.max(1, Math.min(this.maxRows, this.#shown.length));
    this.height = rows * this.#rowHeight + PADDING * 2;
    this.invalidate();
  }

  get entries(): readonly NavEntry[] {
    return this.#shown;
  }

  get query(): string {
    return this.#query;
  }
  set query(v: string) {
    if (this.#query === v) return;
    this.#query = v;
    this.#applyFilter();
    this.invalidate();
  }

  get highlightIndex(): number {
    return this.#highlight;
  }

  #applyFilter(): void {
    this.#shown = filterNavigationIndex(this.#entries, this.#query);
    // Land on something selectable rather than on a window heading, so Enter
    // straight after typing goes where the typing pointed.
    this.#highlight = Math.min(this.#highlight, Math.max(0, this.#shown.length - 1));
    this.#scrollToHighlight();
  }

  #visibleRows(): number {
    return this.#rowHeight > 0 ? Math.max(1, Math.floor((this.height - PADDING * 2) / this.#rowHeight)) : 1;
  }

  #scrollToHighlight(): void {
    const rows = this.#visibleRows();
    if (this.#highlight < this.#scroll) this.#scroll = this.#highlight;
    else if (this.#highlight >= this.#scroll + rows) this.#scroll = this.#highlight - rows + 1;
    this.#scroll = Math.max(0, Math.min(this.#scroll, Math.max(0, this.#shown.length - rows)));
  }

  #move(delta: number): void {
    if (this.#shown.length === 0) return;
    this.#highlight = Math.max(0, Math.min(this.#shown.length - 1, this.#highlight + delta));
    this.#scrollToHighlight();
    this.invalidate();
  }

  /** Go to the highlighted entry: raise its window, focus it, show it. */
  choose(index = this.#highlight): void {
    const entry = this.#shown[index];
    const desktop = this.desktop;
    if (!entry || !desktop) return;
    desktop.closePopup(this);

    const win = windowOf(entry);
    // Activating first, because activation restores that window's own last
    // focus - and would otherwise immediately overwrite the focus set here.
    if (win) desktop.activate(win);
    if (entry.control.focusable) desktop.focusManager.set(entry.control);
    revealIn(entry.control);
    this.onChosen?.(entry);
    desktop.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    switch (e.key) {
      case "ArrowDown":
        this.#move(1);
        break;
      case "ArrowUp":
        this.#move(-1);
        break;
      case "PageDown":
        this.#move(this.#visibleRows());
        break;
      case "PageUp":
        this.#move(-this.#visibleRows());
        break;
      case "Home":
        this.#move(-this.#shown.length);
        break;
      case "End":
        this.#move(this.#shown.length);
        break;
      case "Enter":
        this.choose();
        break;
      case "Backspace":
        this.query = this.#query.slice(0, -1);
        break;
      default:
        return;
    }
    e.handled = true;
  }

  /** Typed characters filter rather than being swallowed. */
  #onTextInput(e: TextInputArgs): void {
    if (!e.text || e.text < " ") return;
    this.query = this.#query + e.text;
    e.handled = true;
  }

  #onPointerDown(e: MouseEventArgs): void {
    const index = this.#scroll + Math.floor((e.y - PADDING) / Math.max(1, this.#rowHeight));
    if (index < 0 || index >= this.#shown.length) return;
    this.#highlight = index;
    this.choose(index);
    e.handled = true;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const body = this.localRect;
    theme.drawNavigationIndexFrame(rc, body);
    this.#rowHeight = theme.metrics.listItemHeight;
    const font = theme.metrics.font;
    const state = this.visualState();

    const window = visibleRowRange(
      this.#scroll * this.#rowHeight,
      body.height - PADDING * 2,
      this.#rowHeight,
      0,
      this.#shown.length,
    );

    for (let i = window.first; i <= window.last; i++) {
      const entry = this.#shown[i]!;
      const row: Rect = {
        x: PADDING,
        y: PADDING + (i - this.#scroll) * this.#rowHeight,
        width: body.width - PADDING * 2,
        height: this.#rowHeight,
      };
      if (row.y + row.height > body.height - PADDING) break;
      const rowState: VisualState = {
        ...state,
        selected: i === this.#highlight,
        active: true,
        focused: i === this.#highlight,
      };
      if (rowState.selected) theme.drawListItem(rc, row, rowState);
      const text = entry.kind === "window" ? windowLabel(entry) : entry.name;
      rc.drawTextInRect(
        text,
        { ...row, x: row.x + entry.depth * INDENT, width: row.width - entry.depth * INDENT },
        font,
        theme.listItemTextColor(rowState),
        theme.metrics.columnPadding,
      );
    }
  }
}

/**
 * Scroll whatever holds this control until it can be seen.
 *
 * Jumping focus to a row twelve screens down a list and leaving the list
 * where it was is the one way this feature can be actively worse than Tab.
 */
function revealIn(control: Control): void {
  const scroller = control.findAncestor((c) => c instanceof ScrollableBase) as ScrollableBase | null;
  if (!scroller) return;
  // Content coordinates: local to the scroller, plus the scroll it has
  // already applied, which is the space `scrollIntoView` reads.
  const local = scroller.fromScreen(control.toScreen({ x: 0, y: 0 }));
  scroller.scrollIntoView({
    x: local.x + scroller.scrollOffsetX,
    y: local.y + scroller.scrollOffsetY,
    width: control.width,
    height: control.height,
  });
}

function windowLabel(entry: NavEntry): string {
  if (entry.minimized) return `${entry.name} (minimized)`;
  return entry.active ? `${entry.name} (active)` : entry.name;
}
